import { useState, useCallback, memo } from "react";
import { useQuery } from "@tanstack/react-query";
import { MultiSelectFilter } from "./MultiSelectFilter";
import { BrandFilter } from "./BrandFilter";
import { DateRangeFilter } from "./DateRangeFilter";
import { FiltersHeader } from "./FiltersHeader";
import { FiltersFooter } from "./FiltersFooter";

import { STATUS_OPTIONS, SITE_TYPE_OPTIONS } from "@/constants/domain-options";
import { dashboardService } from "@/services/DashboardService";
import { useFilterPresets } from "@/hooks/useFilterPresets";
import { useFilterCounts } from "@/hooks/useFilterCounts";
import type { FilterState } from "@/types/dashboard";

const emptyFilters: FilterState = {
  keywords: [],
  geo: [],
  siteTypes: [],
  status: [],
  languages: [],
  dateRange: { from: undefined, to: undefined },
  datePreset: null,
};

const NO_OPTIONS: string[] = [];

interface DashboardFiltersProps {
  initialFilters?: Partial<FilterState>;
  onApply: (filters: FilterState) => void;
  onCancel?: () => void;
}

export const DashboardFilters = memo(
  ({ initialFilters, onApply, onCancel }: DashboardFiltersProps) => {
    const [filters, setFilters] = useState<FilterState>({
      ...emptyFilters,
      ...initialFilters,
    });

    const handleSelectPreset = useCallback((preset: FilterState) => {
      setFilters(preset);
    }, []);

    const {
      presetsOpen,
      setPresetsOpen,
      savedPresets,
      handleSavePreset,
      handleLoadPreset,
      handleDeletePreset,
    } = useFilterPresets(filters, handleSelectPreset);

    const updateFilter = useCallback(<K extends keyof FilterState>(field: K, value: FilterState[K]) => {
      setFilters((prev) => ({ ...prev, [field]: value }));
    }, []);

    const handleClearAll = useCallback(() => {
      setFilters(emptyFilters);
    }, []);

    const handleDone = useCallback(() => {
      onApply(filters);
    }, [filters, onApply]);

    const handleCancel = useCallback(() => {
      if (onCancel) onCancel();
      if (initialFilters) {
        setFilters({ ...emptyFilters, ...initialFilters });
      }
    }, [initialFilters, onCancel]);

    const { data: jetsikOptions } = useQuery({
      queryKey: ["jetsik-filter-options"],
      queryFn: () => dashboardService.getJetsikFilterOptions(),
      staleTime: 1000 * 60 * 5,
    });
    const geoOptions = jetsikOptions?.countries ?? NO_OPTIONS;
    const langOptions = jetsikOptions?.languages ?? NO_OPTIONS;

    const handleKeywordsChange = useCallback((val: string[]) => updateFilter("keywords", val), [updateFilter]);
    const handleGeoChange = useCallback((val: string[]) => updateFilter("geo", val), [updateFilter]);
    const handleSiteTypesChange = useCallback((val: string[]) => updateFilter("siteTypes", val), [updateFilter]);
    const handleStatusChange = useCallback((val: string[]) => updateFilter("status", val), [updateFilter]);
    const handleLanguagesChange = useCallback((val: string[]) => updateFilter("languages", val), [updateFilter]);
    const handleDateRangeChange = useCallback(
      (val: FilterState["dateRange"]) => updateFilter("dateRange", val),
      [updateFilter],
    );
    const handleDatePresetChange = useCallback(
      (val: FilterState["datePreset"]) => updateFilter("datePreset", val),
      [updateFilter],
    );

    const { totalCount, filteredCount, isCounting } = useFilterCounts(filters);

    return (
      <div className="flex flex-col gap-6 rounded-lg border border-border bg-card p-6">
        <FiltersHeader
          isCounting={isCounting}
          filteredCount={filteredCount}
          totalCount={totalCount}
          presetsOpen={presetsOpen}
          onPresetsOpenChange={setPresetsOpen}
          savedPresets={savedPresets}
          onSavePreset={handleSavePreset}
          onLoadPreset={handleLoadPreset}
          onDeletePreset={handleDeletePreset}
        />

        {/* Filter Grid */}
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <BrandFilter
              title="Keyword (Brand)"
              selectedValues={filters.keywords}
              onChange={handleKeywordsChange}
              className="w-full"
            />
            <MultiSelectFilter title="GEO" options={geoOptions} selectedValues={filters.geo} onChange={handleGeoChange} className="w-full" />
            <MultiSelectFilter title="Site Type" options={SITE_TYPE_OPTIONS} selectedValues={filters.siteTypes} onChange={handleSiteTypesChange} className="w-full" />
            <MultiSelectFilter title="Status" options={STATUS_OPTIONS} selectedValues={filters.status} onChange={handleStatusChange} className="w-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <MultiSelectFilter title="Language" options={langOptions} selectedValues={filters.languages} onChange={handleLanguagesChange} className="w-full" />
            <div className="hidden md:block col-span-3"></div>
          </div>

          <div className="pt-2">
            <DateRangeFilter dateRange={filters.dateRange} onChange={handleDateRangeChange} preset={filters.datePreset} onPresetChange={handleDatePresetChange} />
          </div>
        </div>

        <FiltersFooter onCancel={handleCancel} onDone={handleDone} onClearAll={handleClearAll} />
      </div>
    );
  },
);

DashboardFilters.displayName = "DashboardFilters";